import { useEffect, useState } from "react";

/**
 * Возвращает значение, обновляющееся не чаще чем раз в `delayMs`. Полезно
 * для live-API-вызовов на ввод текста — debouncing предотвращает поход на
 * бэкенд на каждый keystroke.
 *
 * Использование:
 * ```ts
 * const [text, setText] = useState("");
 * const debouncedText = useDebouncedValue(text, 700);
 * useQuery({ queryKey: [..., debouncedText], queryFn: ... });
 * ```
 *
 * При unmount таймер очищается; повторные обновления `value` сбрасывают
 * предыдущий таймер.
 */
export function useDebouncedValue<T>(value: T, delayMs: number = 500): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  return debounced;
}
